import type { IObservableArray } from 'mobx';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Flight, Goal as GoalModel } from '@feathr/blackbox';
import { Campaign, MAX_FLIGHT_GOALS, MAX_GOALS } from '@feathr/blackbox';
import { Alert, ButtonValid, EmptyState } from '@feathr/components';
import Goal, { GoalProvider } from '@feathr/extender/components/Goal';
import { useLocalUrl, useStore } from '@feathr/extender/state';
import { flattenErrors, getIconForAction } from '@feathr/hooks';
import type { TValidateGrouped } from '@feathr/rachis';

import { AddGoalsV1 } from './AddGoalsV1';
import { getGoalSegments, validateStepGoals } from './StepGoals.utils';

import * as styles from './AddGoalsV1/AddGoalsV1.css';

interface IButtonProps {
  goals: IObservableArray<GoalModel>;
  onNext?: () => void;
}

interface IProps extends IButtonProps {
  onPrev?: () => void;
  parent: Campaign | Flight;
  goalKind: 'campaign' | 'flight';
  disabled?: boolean;
}

export interface IStepGoalsErrors extends TValidateGrouped {
  goals?: string[];
}

function AddGoals({ parent, goalKind, disabled = false, goals }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { Goals, Segments } = useStore();
  const localUrl = useLocalUrl();

  const segments = getGoalSegments(goals, Segments);
  const validationErrors = validateStepGoals({ goals, goalSegments: segments, t });

  function addGoal(): void {
    const model = Goals.create({
      parent: parent.id,
      kind: goalKind,
      name: `New ${goalKind} goal`,
      conv_type: undefined,
    });
    runInAction(() => {
      goals.push(model);
    });
  }

  function removeGoal(goal: GoalModel): void {
    if (!goal.isEphemeral) {
      goal.set({ is_archived: true });
    } else {
      goals.remove(goal);
      Goals.remove(goal.id);
    }
  }

  const stats = parent.stats;

  const hasMaxGoals = goals.length >= MAX_GOALS;
  const isPublishedCampaign = parent instanceof Campaign && parent.isPublished;
  const hideAddButton = goalKind === 'flight' && goals.length >= MAX_FLIGHT_GOALS;

  const addGoalsButton = (
    <ButtonValid
      disabled={hasMaxGoals ? true : undefined}
      errors={flattenErrors(validationErrors)}
      id={'addGoal'}
      key={'add'}
      name={'add_goal'}
      onClick={addGoal}
      prefix={getIconForAction('add')}
      tooltip={hasMaxGoals ? [t('Campaigns are limited to 5 goals.')] : undefined}
      type={'secondary'}
    >
      {goals.length >= 1 ? t('Add additional goal') : t('Add goal')}
    </ButtonValid>
  );

  return (
    <>
      {/* Display a warning that conversions may need to be rerun if a goal is added to published campaign */}
      {/* TODO: ask about this alert when it comes to the recommendation modal */}
      {isPublishedCampaign && (
        <Alert
          className={styles.alert}
          description={t(
            'Switching groups will cause existing conversions on this campaign to be lost. To apply changes to past data, select "Recalculate conversions" from the conversions table on this campaign\'s report.',
          )}
          title={t('Editing the goals of this campaign will make existing conversions inaccurate.')}
          type={'warning'}
        >
          <a href={localUrl(parent.getItemUrl())} target={'_blank'}>
            {t('View campaign report')}
          </a>
        </Alert>
      )}

      <>
        {goals.filter((g) => !g.get('is_archived')).length > 0 ? (
          <GoalProvider
            disabled={disabled}
            goals={goals}
            handleRemove={removeGoal}
            showWarning={!!stats?.conversions?.full?.num}
          >
            {goals
              .filter((g) => !g.get('is_archived'))
              .map((goal, index) => {
                return <Goal goal={goal} key={goal.id || index} />;
              })}
            <div>{!hideAddButton && addGoalsButton}</div>
          </GoalProvider>
        ) : (
          <EmptyState
            description={t('This campaign will not track conversions.')}
            label={t('No goals added')}
            theme={'neutral'}
          >
            {addGoalsButton}
          </EmptyState>
        )}
      </>
    </>
  );
}

AddGoals.V1 = AddGoalsV1;

export default observer(AddGoals);
